/**
 * The job status machine.
 *
 * This file is the ONLY place that knows which job status transitions are
 * legal. The API enforces it, the web app greys out buttons with it, and the
 * mobile app decides what a tech may tap with it. If you find a second copy of
 * these rules anywhere, delete that copy.
 *
 * Note on "on my way": the tech's en-route tap is deliberately NOT a status.
 * The spec fixes the status set, and en-route is a notification event plus a
 * timestamp on the job (`enRouteAt`), not a state the office needs to reconcile.
 * See `JOB_ACTIONS` below.
 */
export const JOB_STATUSES = [
    'unscheduled',
    'scheduled',
    'in_progress',
    'completed',
    'invoiced',
    'paid',
    'cancelled',
];
export const JOB_STATUS_LABELS = {
    unscheduled: 'Unscheduled',
    scheduled: 'Scheduled',
    in_progress: 'In progress',
    completed: 'Completed',
    invoiced: 'Invoiced',
    paid: 'Paid',
    cancelled: 'Cancelled',
};
/** Tailwind-ish token names; the clients map these to their own palettes. */
export const JOB_STATUS_TONE = {
    unscheduled: 'slate',
    scheduled: 'blue',
    in_progress: 'amber',
    completed: 'green',
    invoiced: 'violet',
    paid: 'emerald',
    cancelled: 'red',
};
const requiresSchedule = (ctx) => {
    if (!ctx.assignedTechId)
        return 'Assign a technician before scheduling';
    if (!ctx.scheduledStart || !ctx.scheduledEnd)
        return 'Set a scheduled window before scheduling';
    return null;
};
export const JOB_TRANSITIONS = {
    unscheduled: [
        { to: 'scheduled', guard: requiresSchedule },
        { to: 'cancelled' },
    ],
    scheduled: [
        { to: 'in_progress' },
        { to: 'unscheduled' },
        { to: 'cancelled' },
    ],
    in_progress: [
        { to: 'completed' },
        { to: 'scheduled', guard: requiresSchedule },
        { to: 'cancelled' },
    ],
    completed: [
        {
            to: 'invoiced',
            guard: (ctx) => (ctx.hasInvoice === false ? 'Create an invoice first' : null),
        },
        { to: 'in_progress' },
        { to: 'cancelled' },
    ],
    invoiced: [
        {
            to: 'paid',
            guard: (ctx) => ctx.invoiceBalanceCents != null && ctx.invoiceBalanceCents > 0
                ? 'Invoice still has an outstanding balance'
                : null,
        },
        { to: 'completed' },
    ],
    paid: [{ to: 'invoiced' }],
    cancelled: [{ to: 'unscheduled' }],
};
export const TERMINAL_JOB_STATUSES = ['paid', 'cancelled'];
/*
 * There is deliberately no role gate here.
 *
 * Whether a job may move is a question about the job, not about who is
 * asking: `invoiced` needs an invoice to exist, `paid` needs the balance to
 * be nil, `scheduled` needs a technician and a window. Those guards hold
 * whoever presses the button, so a technician settling up at the door cannot
 * mark a job paid that nobody paid — not because of their role, but because
 * the money is not there.
 *
 * What the role still decides is *which* jobs somebody can reach at all: a
 * technician sees only jobs assigned to them, and the API checks that before
 * it gets here.
 */
/**
 * A transition said as the button that performs it, rather than as the state
 * it lands in. "Completed" is a fact about a job; "Mark it complete" is a
 * thing a person does, and a button should say the second one.
 */
export const JOB_TRANSITION_LABELS = {
    unscheduled: 'Move back to the backlog',
    scheduled: 'Put back on the schedule',
    in_progress: 'Start the job',
    completed: 'Mark it complete',
    invoiced: 'Mark it invoiced',
    paid: 'Mark it paid',
    cancelled: 'Cancel the job',
};
/** Statuses whose jobs still occupy a slot on the dispatch board. */
export const OCCUPYING_JOB_STATUSES = [
    'scheduled',
    'in_progress',
    'completed',
    'invoiced',
    'paid',
];
export function isJobStatus(value) {
    return typeof value === 'string' && JOB_STATUSES.includes(value);
}
export function allowedTransitions(from) {
    return (JOB_TRANSITIONS[from] ?? []).map((r) => r.to);
}
export function checkTransition(from, to, ctx = {}) {
    if (from === to) {
        return { ok: false, code: 'same_status', reason: `Job is already ${JOB_STATUS_LABELS[to]}` };
    }
    const rule = (JOB_TRANSITIONS[from] ?? []).find((r) => r.to === to);
    if (!rule) {
        return {
            ok: false,
            code: 'illegal_transition',
            reason: `Cannot move a job from ${JOB_STATUS_LABELS[from]} to ${JOB_STATUS_LABELS[to]}`,
        };
    }
    const blocked = rule.guard?.(ctx) ?? null;
    if (blocked)
        return { ok: false, code: 'guard_failed', reason: blocked };
    return { ok: true };
}
export function canTransition(from, to, ctx = {}) {
    return checkTransition(from, to, ctx).ok;
}
export class JobTransitionError extends Error {
    code;
    from;
    to;
    constructor(from, to, code, reason) {
        super(reason);
        this.name = 'JobTransitionError';
        this.code = code;
        this.from = from;
        this.to = to;
    }
}
export function assertTransition(from, to, ctx = {}) {
    const result = checkTransition(from, to, ctx);
    if (!result.ok)
        throw new JobTransitionError(from, to, result.code, result.reason);
}
export const JOB_ACTIONS = {
    en_route: { label: "On my way", targetStatus: null },
    start: { label: 'Start job', targetStatus: 'in_progress' },
    complete: { label: 'Complete', targetStatus: 'completed' },
    pause: { label: 'Back to scheduled', targetStatus: 'scheduled' },
};
/** What the mobile app should show as the single primary button. */
export function primaryFieldAction(status, enRouteAt) {
    if (status === 'scheduled')
        return enRouteAt ? 'start' : 'en_route';
    if (status === 'in_progress')
        return 'complete';
    return null;
}
//# sourceMappingURL=job-status.js.map